import React, {useEffect, useState} from 'react';
import Breadcrumbs from '../Components/Breadcrumbs';
import Table from '../Components/Tables';
import {ConfirmationProvider} from '../Components/ConfirmationContext';
import {AlertProvider} from '../Components/AlertContext';
import {formatDateUTC} from '../utils/formatDateUTC';

import {AuditOperationsApiProvider, useAuditOperationsApi} from "../Services/AuditOperationsApiProvider";
import PeriodSelector from "../Components/PeriodSelector";
import CustomDateRangePicker from "../Components/CustomDateRangePicker";
import {useNavigate, useParams} from "react-router-dom";
import Button from "../Components/Buttons";

function AuditOperationsHub({canEdit}) {
  const {getAuditOperations, getAuditOperationTypes, exportAuditOperations } = useAuditOperationsApi();
  const { organizationId } = useParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auditOperations, setAuditOperations] = useState([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  const [selectedPeriod, setSelectedPeriod] = useState("all_time");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);

  const [selectedTypes, setSelectedTypes] = useState([]);

  const columns = [
    {name: 'ID', selector: (row) => row.key, sortable: true},
    {name: 'User Key', selector: (row) => row.user_key, sortable: true},
    {name: 'Operation Type', selector: (row) => row.operation_type, sortable: true},
    {name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true},
    {name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true},
  ];

  const handlePeriodChange = (e) => setSelectedPeriod(e.target.value);

  const getDateRangeByPeriod = (period) => {
    const today = new Date();
    let startDate = null;

    switch (period) {
      case "all_time":
        startDate = new Date(2000, 0, 1);
        break;
      case "year":
        startDate = new Date();
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      case "month":
        startDate = new Date();
        startDate.setMonth(today.getMonth() - 1);
        break;
      case "day":
        startDate = new Date();
        startDate.setDate(today.getDate() - 1);
        break;
      default:
        startDate = null;
    }
    return {
      start_date: startDate ? startDate.toISOString() : null,
      end_date: today.toISOString()
    };
  };

  const fetchAuditOperationTypes = async () => {
    try {
      return await getAuditOperationTypes();
    } catch (error) {
      console.error("Error fetching audit operation types:", error);
    }
  }

  const fetchAuditOperations = async (skip, limit, type=null) => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      let start_date, end_date;

      if (selectedPeriod === "custom") {
        if (!customFrom || !customTo) return;
        start_date = customFrom;
        end_date = customTo;
      } else {
        ({start_date, end_date} = getDateRangeByPeriod(selectedPeriod));
      }

      setLoading(true);
      const response = await getAuditOperations(skip, limit, start_date, end_date, type);
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
      setItemsPerPage(limit);
    } catch (error) {
      console.error("Error fetching audit operations:", error);
      setError("Failed to load audit operations");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAuditOperations(0, itemsPerPage);
  }, [customFrom, customTo, selectedPeriod]);

  useEffect(() => {
    const loadTypes = async () => {
      const types = await fetchAuditOperationTypes();
      if (!types) return;
      const sortedTypes = [...types].sort((a, b) => a.localeCompare(b));
      const options = sortedTypes.map((type) => ({ label: type, value: type }));
      setSearchOptions(options);
    };
    loadTypes();
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/audit-operations/${row.key}`);
  };

  const handleTypeChange = async (skip, limit, type) => {
    setSelectedTypes(Array.isArray(type) ? type : (type ? [type] : []));
    await fetchAuditOperations(skip, limit, type);
  };

const handleExport = async () => {
  try {
    let start_date = null;
    let end_date = null;

    if (selectedPeriod === "custom") {
      if (!customFrom || !customTo) return;
      start_date = customFrom;
      end_date = customTo;
    } else {
      ({ start_date, end_date } = getDateRangeByPeriod(selectedPeriod));
    }

    // якщо використовуєш мультиселект типів — передай сюди масив або null
    await exportAuditOperations(start_date, end_date, selectedTypes ?? null);
  } catch (error) {
    console.error("Error exporting audit operations:", error);
  }
};

  return (
    <div className="container">
      <Breadcrumbs/>
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          <div className={"mb-3 p-2"}>
            <PeriodSelector selectedPeriod={selectedPeriod} onChange={handlePeriodChange}/>
            {selectedPeriod === "custom" && (
              <CustomDateRangePicker
                customFrom={customFrom}
                customTo={customTo}
                setCustomFrom={setCustomFrom}
                setCustomTo={setCustomTo}
              />
            )}
          </div>
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title="Audit Operations"
                columns={columns}
                data={auditOperations}
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={itemsPerPage}
                onRowClicked={handleRowClicked}
                paginationTotalRows={totalAuditOperations}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchAuditOperations}
                getRowUrl={(row) => `/organizations/${organizationId}/audit-operations/${row.key}`}
                onChangeFilter={handleTypeChange}
                typeSearch = 'multiselect'
                searchOptions={searchOptions}
                headerActions={
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleExport}
                  >
                    Export JSON
                  </Button>
                }

              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditOperationsWrapper({canEdit}) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <AuditOperationsApiProvider>
          <AuditOperationsHub canEdit={canEdit}/>
        </AuditOperationsApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default AuditOperationsWrapper;